import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/builds/prizmdoc/accusoft-pdf-viewer/nostromo/docs-site/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`This guide explains how to configure a reverse proxy so that your application can proxy requests to PrizmDoc Viewer.`}</p>
    <h2>{`Prerequisites`}</h2>
    <p>{`With `}<a parentName="p" {...{
        "href": "https://www.accusoft.com/products/prizmdoc-suite/prizmdoc-viewer/",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`PrizmDoc Viewer`}</a>{` you can convert many different document types to `}<inlineCode parentName="p">{`ViewingPackages`}</inlineCode>{` which can then be viewed with the PDF Viewer. This How-To will require access to a PrizmDoc Server instance. The PDF Viewer requires access to the PrizmDoc Server to request content for Viewing Sessions for previously created Viewing Packages. See the topic, `}<a parentName="p" {...{
        "href": "/accusoft-pdf-viewer/latest/how-to/view-a-viewing-package/"
      }}>{`How-To: View a Viewing Package`}</a>{` for instructions on the creation of a Viewing Package and viewing it’s content.`}</p>
    <h2>{`Directions`}</h2>
    <p>{`There are many different tools available to provide an application with a reverse proxy to a back-end server depending on which framework you are using. Examples include `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/http-proxy-middleware",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`http-proxy-middleware`}</a>{` for `}<a parentName="p" {...{
        "href": "https://nodejs.org/",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`node.js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://expressjs.com/",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`express`}</a>{`, `}<a parentName="p" {...{
        "href": "https://www.nuget.org/packages/SharpReverseProxy/",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`SharpReverseProxy`}</a>{` for ASP.NET, `}<a parentName="p" {...{
        "href": "https://github.com/Netflix/zuul",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`Netflix Zuul`}</a>{` for Java Spring. For framework agnostic solutions, IIS and `}<a parentName="p" {...{
        "href": "https://www.nginx.com/",
        "target": "_blank",
        "rel": "noreferrer"
      }}>{`NGINX`}</a>{` are popular tools. This example will demonstrate configuring a reverse proxy using NGINX.`}</p>
    <p>{`PDF Viewer requires access to PrizmDoc Server to request content for a Viewing Package. Therefore, NGINX will provide both hosting for the application and a reverse proxy for the requests to PrizmDoc Server. This example `}<inlineCode parentName="p">{`nginx.conf`}</inlineCode>{` file will configure NGINX to serve the application `}<inlineCode parentName="p">{`root`}</inlineCode>{` from an `}<inlineCode parentName="p">{`/app/www`}</inlineCode>{` directory:`}</p>
    <pre {...{
      "className": "grvsc-container grvsc-has-line-highlighting default-dark",
      "data-language": "txt",
      "data-index": "0"
    }}><code parentName="pre" {...{
        "className": "grvsc-code"
      }}><span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`events {}`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}></span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`http {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`  server {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    listen 80;`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}></span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    location / {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`      root /app/www;`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    }`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`  }`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`}`}</span></span></code></pre>
    <p>{`Note that this is a simple example to show how to set up a reverse proxy with NGINX. In a production environment, care should be taken to ensure good security and best practices.`}</p>
    <h2>{`Adding the Reverse Proxy`}</h2>
    <p>{`Adding the section below to the `}<inlineCode parentName="p">{`nginx.conf`}</inlineCode>{` file will enable requests to proxy through a `}<inlineCode parentName="p">{`prizmdoc`}</inlineCode>{` route to a PrizmDoc server using `}<inlineCode parentName="p">{`proxy_pass`}</inlineCode>{`:`}</p>
    <pre {...{
      "className": "grvsc-container grvsc-has-line-highlighting default-dark",
      "data-language": "txt",
      "data-index": "1"
    }}><code parentName="pre" {...{
        "className": "grvsc-code"
      }}><span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`events {}`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}></span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`http {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`  server {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    listen 80;`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}></span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    location / {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`      root /app/www;`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    }`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}></span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    location /prizmdoc/ {`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`      proxy_pass https://my-prizmdoc-server.my-domain.com/;`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line grvsc-line-highlighted"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`    }`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`  }`}</span></span>{`
`}<span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}>{`}`}</span></span></code></pre>
    <p>{`The example can then be started with the command line below:`}</p>
    <pre {...{
      "className": "grvsc-container default-dark",
      "data-language": "bash",
      "data-index": "2"
    }}><code parentName="pre" {...{
        "className": "grvsc-code"
      }}><span parentName="code" {...{
          "className": "grvsc-line"
        }}><span parentName="span" {...{
            "className": "grvsc-source"
          }}><span parentName="span" {...{
              "className": "mtk1"
            }}>{`nginx -c /app/nginx.conf`}</span></span></span></code></pre>
    <p>{`Once NGINX is started, all `}<inlineCode parentName="p">{`/prizmdoc/*`}</inlineCode>{` requests will be proxied to the `}<inlineCode parentName="p">{`https://my-prizmdoc-server.my-domain.com/`}</inlineCode>{`.`}</p>

    <style {...{
      "className": "grvsc-styles"
    }}>{`
  .grvsc-container {
    overflow: auto;
    position: relative;
    -webkit-overflow-scrolling: touch;
    padding-top: 1rem;
    padding-top: var(--grvsc-padding-top, var(--grvsc-padding-v, 1rem));
    padding-bottom: 1rem;
    padding-bottom: var(--grvsc-padding-bottom, var(--grvsc-padding-v, 1rem));
    border-radius: 8px;
    border-radius: var(--grvsc-border-radius, 8px);
    font-feature-settings: normal;
    line-height: 1.4;
  }
  
  .grvsc-code {
    display: table;
  }
  
  .grvsc-line {
    display: table-row;
    box-sizing: border-box;
    width: 100%;
    position: relative;
  }
  
  .grvsc-line > * {
    position: relative;
  }
  
  .grvsc-gutter-pad {
    display: table-cell;
    padding-left: 0.75rem;
    padding-left: calc(var(--grvsc-padding-left, var(--grvsc-padding-h, 1.5rem)) / 2);
  }
  
  .grvsc-gutter {
    display: table-cell;
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
  }
  
  .grvsc-gutter::before {
    content: attr(data-content);
  }
  
  .grvsc-source {
    display: table-cell;
    padding-left: 1.5rem;
    padding-left: var(--grvsc-padding-left, var(--grvsc-padding-h, 1.5rem));
    padding-right: 1.5rem;
    padding-right: var(--grvsc-padding-right, var(--grvsc-padding-h, 1.5rem));
  }
  
  .grvsc-source:empty::after {
    content: ' ';
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
  }
  
  .grvsc-gutter + .grvsc-source {
    padding-left: 0.75rem;
    padding-left: calc(var(--grvsc-padding-left, var(--grvsc-padding-h, 1.5rem)) / 2);
  }
  
  /* Line transformer styles */
  
  .grvsc-has-line-highlighting > .grvsc-code > .grvsc-line::before {
    content: ' ';
    position: absolute;
    width: 100%;
  }
  
  .grvsc-line-diff-add::before {
    background-color: var(--grvsc-line-diff-add-background-color, rgba(0, 255, 60, 0.2));
  }
  
  .grvsc-line-diff-del::before {
    background-color: var(--grvsc-line-diff-del-background-color, rgba(255, 0, 20, 0.2));
  }
  
  .grvsc-line-number {
    padding: 0 2px;
    text-align: right;
    opacity: 0.7;
  }
  
  .default-dark {
    background-color: #1E1E1E;
    color: #D4D4D4;
  }
  .default-dark .mtk1 { color: #D4D4D4; }
  .default-dark .grvsc-line-highlighted::before {
    background-color: var(--grvsc-line-highlighted-background-color, rgba(255, 255, 255, 0.1));
    box-shadow: inset var(--grvsc-line-highlighted-border-width, 4px) 0 0 0 var(--grvsc-line-highlighted-border-color, rgba(255, 255, 255, 0.5));
  }
`}</style>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      